import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Vendor, Product } from './types';
import config from '../../config';

interface VendorFormData {
  _id?: string;
  vendorId: string;
  vendorName: string;
  products: Product[];
}

interface VendorFormProps {
  vendor: Vendor | null;
  onSave: () => void;
  onCancel: () => void;
}

const VendorForm: React.FC<VendorFormProps> = ({ vendor, onSave, onCancel }) => {
  const [formData, setFormData] = useState<VendorFormData>({
    vendorId: '',
    vendorName: '',
    products: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');

  const baseURL = config.API_BASE_URL;

  useEffect(() => {
    if (vendor) {
      setFormData(vendor);
    }
  }, [vendor]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProductChange = (index: number, field: keyof Product, value: string) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map((product, i) => 
        i === index ? { ...product, [field]: value } : product
      )
    }));
  };

  const addProduct = () => {
    setFormData(prev => ({
      ...prev,
      products: [
        ...prev.products,
        {
          materialDesc: '',
          materialId: '',
          vendorProductName: ''
        }
      ]
    }));
  };

  const removeProduct = (index: number) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      if (vendor) {
        // Update existing vendor
        await axios.put(`${baseURL}/vendors/${vendor._id}`, formData, config);
      } else {
        // Create new vendor
        await axios.post(`${baseURL}/vendors`, formData, config);
      }

      onSave();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save vendor');
      console.error('Error saving vendor:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter and paginate products
  const filteredProducts = formData.products.filter(product =>
    product.materialDesc.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.materialId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.vendorProductName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const isFormValid = () => {
    return formData.vendorId.trim() && formData.vendorName.trim();
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content vendor-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{vendor ? 'Edit Vendor' : 'Add New Vendor'}</h3>
          <button className="modal-close" onClick={onCancel}>✕</button>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="vendor-form">
          <div className="form-section">
            <h4>Vendor Information</h4>
            <div className="form-group">
              <label htmlFor="vendorId">
                Vendor ID <span className="required">*</span>
              </label>
              <input
                type="text"
                id="vendorId"
                name="vendorId"
                value={formData.vendorId}
                onChange={handleInputChange}
                required
                placeholder="e.g., V001"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="vendorName">
                Vendor Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="vendorName"
                name="vendorName"
                value={formData.vendorName}
                onChange={handleInputChange}
                required
                placeholder="e.g., ABC Corporation"
                className="form-input"
              />
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h4>Product Mappings</h4>
              <div className="product-controls">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input search-input"
                  />
                  <span className="search-icon">🔍</span>
                </div>
                <div className="product-count">
                  {filteredProducts.length} of {formData.products.length} products
                </div>
              </div>
            </div>

            {formData.products.length === 0 ? (
              <div className="empty-products">
                <p>No products added yet. Click "Add Product" below to start.</p>
              </div>
            ) : (
              <>
                <div className="products-list">
                  {currentProducts.map((product, index) => {
                    const originalIndex = formData.products.findIndex(p => 
                      p.materialDesc === product.materialDesc && 
                      p.materialId === product.materialId && 
                      p.vendorProductName === product.vendorProductName
                    );
                    
                    return (
                      <div key={originalIndex} className="product-row">
                        <div className="product-fields">
                          <div className="form-group">
                            <label>Material Description</label>
                            <input
                              type="text"
                              value={product.materialDesc}
                              onChange={(e) => handleProductChange(originalIndex, 'materialDesc', e.target.value)}
                              placeholder="Client's product name"
                              className="form-input"
                            />
                          </div>

                          <div className="form-group">
                            <label>Material ID</label>
                            <input
                              type="text"
                              value={product.materialId}
                              onChange={(e) => handleProductChange(originalIndex, 'materialId', e.target.value)}
                              placeholder="Product ID in client system"
                              className="form-input"
                            />
                          </div>

                          <div className="form-group">
                            <label>Vendor Product Name</label>
                            <input
                              type="text"
                              value={product.vendorProductName}
                              onChange={(e) => handleProductChange(originalIndex, 'vendorProductName', e.target.value)}
                              placeholder="Vendor's product name"
                              className="form-input"
                            />
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn btn-sm btn-danger remove-product"
                          onClick={() => removeProduct(originalIndex)}
                          title="Remove product"
                        >
                          🗑️
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination-controls">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ← Previous
                    </button>
                    <span className="page-info">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Add Product Button at Bottom */}
            <div className="add-product-section">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addProduct}
              >
                <i className="bi bi-plus-circle me-1"></i>
                + Add Product
              </button>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !isFormValid()}
            >
              {loading ? 'Saving...' : (vendor ? 'Update Vendor' : 'Create Vendor')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorForm;
